/* eslint-disable max-lines */
import { TableColumn } from '@fl/cmsch-fe-library';
import React, { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router';
import { BreedingFemale } from 'api/gen/BreedingFemale';
import { MotherLinkProps } from 'app/animal-browser/types/mother-link-props';
import { getAnimalBrowserLinkForTable } from 'app/animal-browser/utils/get-animal-browser-link';
import { getBullBrowserLink } from '../../utils/get-bull-browser-link';

/**
 * Builds the column configuration for the cow browser table.
 *
 * @param handleMotherLink Click handler used for the mother-ear-tag link column.
 * @returns The cow-browser table columns.
 */
// eslint-disable-next-line max-lines-per-function
export const getCowBrowserColumns = (handleMotherLink: (row: MotherLinkProps) => (event: MouseEvent) => void): Array<TableColumn<BreedingFemale>> => [{
  field: 'earTag',
  type: 'hyperlink',
  filterTypes: ['betweenEarTags'],
  valueToUrl: getAnimalBrowserLinkForTable,
  openInNewTab: true
}, {
  field: 'birthDate',
  type: 'date',
  dateFormat: 'date',
  filterTypes: ['null']
}, {
  field: 'breed',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'name',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'proficiencyTest',
  type: 'optionalBoolean'
}, {
  field: 'genotype',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'selectionIndex',
  type: 'decimal',
  fixedDecimals: 1,
  filterTypes: ['null']
}, {
  field: 'fatherLineRegistry',
  type: 'router',
  valueToLink: getBullBrowserLink('fatherLineRegistry'),
  openInNewTab: true,
  filterTypes: ['null']
}, {
  field: 'fatherName',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'motherEarTag',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'betweenEarTags', 'null'],
  type: 'string',
  formatValue: (value: string, row: MotherLinkProps): ReactNode => {
    const handleClick = handleMotherLink(row);
    return <Link to="#" onClick={handleClick} onAuxClick={handleClick}>
                    {value}
                </Link>;
  }
}, {
  field: 'mothersFatherLineRegistry',
  type: 'router',
  filterTypes: ['null'],
  valueToLink: getBullBrowserLink('mothersFatherLineRegistry')
}, {
  field: 'mothersFatherName',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'calveDate',
  type: 'date',
  dateFormat: 'date',
  filterTypes: ['null']
}, {
  field: 'lastMatingDate',
  type: 'date',
  dateFormat: 'date',
  filterTypes: ['null']
}, {
  field: 'inseminationOrder',
  type: 'number',
  filterTypes: ['null']
}, {
  field: 'servicePeriod',
  type: 'number',
  filterTypes: ['null']
}, {
  field: 'pregnant',
  type: 'optionalBoolean'
}, {
  field: 'proficiencyTestDate',
  type: 'date',
  dateFormat: 'date',
  filterTypes: ['null']
}, {
  field: 'maxLactationOrder',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationDays',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationMilkKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationFatKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationFatPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'maxLactationProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationProteinPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'avgLactationCount',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'avgLactationDays',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'avgLactationMilkKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'avgLactationFatKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'avgLactationFatPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'avgLactationProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'avgLactationProteinPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lastLactationOrder',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lastLactationDays',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lastLactationMilkKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lastLactationFatKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lastLactationFatPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lastLactationProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lastLactationProteinPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lastLactationDate',
  type: 'date',
  verticalName: true,
  dateFormat: 'date',
  filterTypes: ['null']
}, {
  field: 'currentLactationOrder',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationDays',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationMilkKgSum',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationMilkKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationFatKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationFatPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'currentLactationProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationProteinPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'currentLactationLactoseKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationLactosePercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'currentLactationSomaticCells',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'currentLactationUrea',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 1,
  filterTypes: ['null']
}, {
  field: 'changeCode',
  type: 'number',
  filterTypes: ['null']
}, {
  field: 'studbookSection',
  type: 'string',
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'exterior',
  type: 'string',
  verticalName: true,
  customFiltersOnly: true,
  filterTypes: ['containsString', 'null']
}, {
  field: 'milkStrengthToFrame',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'bodyStructureToMusculature',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'extremities',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'udder',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'alive',
  type: 'optionalBoolean',
  verticalName: true
}, {
  field: 'lifelongLactationDaysSum',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongStandardizedLactationDays',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationMilkKgSum',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationMilkKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationFatKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationFatPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'lifelongLactationProteinPercentage',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'daysFromBirth',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'milkKgAvgPerDay',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'milkKgAvgPerLactationDay',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lastMilkYield',
  type: 'decimal',
  verticalName: true,
  fixedDecimals: 2,
  filterTypes: ['null']
}, {
  field: 'lastLactationFatProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}, {
  field: 'maxLactationFatProteinKg',
  type: 'number',
  verticalName: true,
  filterTypes: ['null']
}];